/**
 * useDocumentSelector - Hook compartido para lógica de selección de documentos
 * Extrae la lógica común de selección de documentos para DocumentContextSelector
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiService, type Document } from '../services/api';
import type { MentionType } from '../components/chat/MentionTypes';
import { formatPeriod, formatPeriodForFilter } from '../utils/formatPeriod';

export interface DocumentFilters {
  company?: string | string[];
  document_type?: string | string[];
  document_period?: string | string[];
  shared_category?: string | string[];
}

export interface FilterSuggestion {
  value: string;
  display: string;
  score?: number;
}

export type DocumentScope = 'shared' | 'private';

// Persisted user preferences — localStorage keys
const RECENT_DOCS_KEY = 'biblioteca-recent-docs';
const SCOPE_KEY = 'biblioteca-scope';
const SORT_KEY = 'biblioteca-sort';
const MAX_RECENT_DOCS = 50;
const VALID_SORTS = ['-created_at', 'created_at', 'name', '-name', 'company_name', '-company_name'];

export interface RecentDoc {
  id: string;
  name: string;
  scope: 'shared' | 'private';
  company?: string;
  type?: string;
  period?: string;
}

export interface UseDocumentSelectorOptions {
  initialFilters?: DocumentFilters;
  initialSearch?: string;
  selectedDocumentIds: string[];
  onDocumentsSelect: (documentIds: string[]) => void;
  onFiltersChange?: (filters: DocumentFilters) => void;
  onFilterSelect?: (filterType: MentionType, value: string) => void;
  enableLogging?: boolean;
  /** When false, skip all API calls and return empty documents. Default: true */
  enabled?: boolean;
  /** Override scope without persisting to localStorage. Prevents two instances from conflicting. */
  overrideScope?: DocumentScope;
}

export interface UseDocumentSelectorReturn {
  // Estado
  documents: Document[];
  isLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filters: DocumentFilters;
  filterMode: MentionType | null;
  filterSuggestions: FilterSuggestion[];
  isLoadingFilters: boolean;
  searchInContent: boolean;
  setSearchInContent: (value: boolean) => void;
  scopeFilter: DocumentScope;
  setScopeFilter: (scope: DocumentScope) => void;
  recentDocs: RecentDoc[];
  inactiveScopeCount: number | null;
  activeScopeCount: number | null;
  sortOrder: string;
  setSortOrder: (order: string) => void;

  // Funciones de documentos
  loadDocuments: (search?: string) => Promise<void>;
  toggleDocumentSelection: (documentId: string) => void;
  selectDocumentRange: (documentIds: string[]) => void;
  handleSelectAll: () => void;
  handleDeselectAll: () => void;

  // Funciones de filtros
  handleFilterClick: (filterType: MentionType) => void;
  handleFilterValueSelect: (value: string) => void;
  handleRemoveFilter: (filterType: MentionType, e?: React.MouseEvent) => void;
  handleRemoveCompanyValue: (value: string, e?: React.MouseEvent) => void;
  handleClearAllFilters: () => void;
  handleBackToDocuments: () => void;
  loadFilterSuggestions: (filterType: MentionType, search: string) => void;

  // Utilidades
  formatDocumentMetadata: (doc: Document) => string;
  hasFilters: boolean;
  selectedCount: number;
}

export const useDocumentSelector = (
  options: UseDocumentSelectorOptions
): UseDocumentSelectorReturn => {
  const {
    initialFilters = {},
    initialSearch = '',
    selectedDocumentIds,
    onDocumentsSelect,
    onFiltersChange,
    onFilterSelect,
    enableLogging = false,
    enabled = true,
    overrideScope,
  } = options;

  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [filters, setFilters] = useState<DocumentFilters>(initialFilters);
  const [filterMode, setFilterMode] = useState<MentionType | null>(null);
  const [filterSuggestions, setFilterSuggestions] = useState<FilterSuggestion[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [searchInContent, setSearchInContent] = useState(false);
  const [scopeFilter, setScopeFilter] = useState<DocumentScope>(() => {
    if (overrideScope) return overrideScope;
    try {
      const saved = localStorage.getItem(SCOPE_KEY);
      return saved === 'private' ? 'private' : 'shared';
    } catch {
      return 'shared';
    }
  });
  const [inactiveScopeCount, setInactiveScopeCount] = useState<number | null>(null);
  const [activeScopeCount, setActiveScopeCount] = useState<number | null>(null);
  const [sortOrder, setSortOrder] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(SORT_KEY);
      return saved && VALID_SORTS.includes(saved) ? saved : '-created_at';
    } catch {
      return '-created_at';
    }
  });
  const [recentDocs, setRecentDocs] = useState<RecentDoc[]>(() => {
    try {
      const saved = localStorage.getItem(RECENT_DOCS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Persist scope & sort preferences (skip when overrideScope is set to avoid conflicts)
  useEffect(() => {
    if (overrideScope) return;
    try {
      localStorage.setItem(SCOPE_KEY, scopeFilter);
    } catch {
      /* ignore */
    }
  }, [scopeFilter, overrideScope]);

  useEffect(() => {
    try {
      localStorage.setItem(SORT_KEY, sortOrder);
    } catch {
      /* ignore */
    }
  }, [sortOrder]);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);
  const selectedDocumentIdsRef = useRef(selectedDocumentIds);
  const onDocumentsSelectRef = useRef(onDocumentsSelect);

  // Mantener refs actualizados
  useEffect(() => {
    selectedDocumentIdsRef.current = selectedDocumentIds;
  }, [selectedDocumentIds]);

  useEffect(() => {
    onDocumentsSelectRef.current = onDocumentsSelect;
  }, [onDocumentsSelect]);

  // Verificar montaje
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Actualizar searchTerm cuando cambia initialSearch
  useEffect(() => {
    if (initialSearch !== undefined && initialSearch !== searchTerm) {
      setSearchTerm(initialSearch);
    }
  }, [initialSearch]);

  // Actualizar filters cuando cambia initialFilters
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setFilters(initialFilters);
    }
  }, [
    initialFilters.company,
    initialFilters.document_type,
    initialFilters.document_period,
    initialFilters.shared_category,
  ]);

  // NOTE: searchInContent is now safe to enable thanks to FAISS semantic search.
  // The backend uses FAISS vector search (O(log n), ~300ms) instead of loading
  // all 549K chunks into memory. We auto-activate it for queries ≥3 chars
  // to provide semantic search (e.g., "retail" → Falabella, Cencosud).
  // Metadata-only search (name, filename, company_name) is used for short queries.

  // Cargar documentos con filtros
  const loadDocuments = useCallback(
    async (search: string = '') => {
      if (!enabled) {
        setDocuments([]);
        setActiveScopeCount(null);
        setIsLoading(false);
        return;
      }
      if (!isMountedRef.current) return;

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      // 8s timeout: abort FAISS semantic search if server is under load,
      // preventing indefinite "Cargando documentos..." spinner.
      const SEARCH_TIMEOUT_MS = 8000;
      const timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          abortControllerRef.current?.abort();
        }
      }, SEARCH_TIMEOUT_MS);

      try {
        if (isMountedRef.current) {
          setIsLoading(true);
        }
        // Auto-activate FAISS semantic search for queries ≥3 chars.
        // Backend uses FAISS (~300ms) instead of loading all chunks (>90s).
        const useSemanticSearch = searchInContent || search.trim().length >= 3;
        const requestParams = {
          company: filters.company,
          type: filters.document_type,
          period: filters.document_period,
          sector: filters.shared_category,
          search: search,
          limit: 200,
          search_in_content: useSemanticSearch,
          scope: scopeFilter,
          // sort is handled client-side via useMemo for instant re-ordering
        };

        const response = await apiService.getFilteredDocuments(requestParams);

        if (
          response.success &&
          isMountedRef.current &&
          !abortControllerRef.current?.signal.aborted
        ) {
          let docs = response.documents || [];

          // Si hay scores de búsqueda (FAISS semantic), ordenar por relevancia (mayor a menor)
          if (useSemanticSearch && docs.some((d: any) => d.search_score !== undefined)) {
            docs = [...docs].sort((a: any, b: any) => {
              const scoreA = a.search_score || 0;
              const scoreB = b.search_score || 0;
              return scoreB - scoreA; // Ordenar descendente (mayor score primero)
            });
          }

          setDocuments(docs);
          setActiveScopeCount(response.count ?? docs.length);

          // Fetch inactive scope count (fire-and-forget, skip when overrideScope — shell manages counts)
          if (!overrideScope) {
            const otherScope = scopeFilter === 'shared' ? 'private' : 'shared';
            apiService
              .getFilteredDocuments({ scope: otherScope, limit: 1 })
              .then((r) => {
                if (r.success && isMountedRef.current) setInactiveScopeCount(r.count);
              })
              .catch(() => {});
          }
        }
      } catch (error: any) {
        if (
          error?.name !== 'AbortError' &&
          isMountedRef.current &&
          !abortControllerRef.current?.signal.aborted
        ) {
          setDocuments([]);
        }
      } finally {
        clearTimeout(timeoutId);
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      filters.company,
      filters.document_type,
      filters.document_period,
      filters.shared_category,
      searchInContent,
      scopeFilter,
      enableLogging,
      enabled,
      overrideScope,
    ]
  );

  // Client-side sort — instant re-ordering without API round-trip.
  // When searching (≥3 chars triggers FAISS), preserve relevance order from backend
  // instead of re-sorting alphabetically which buries the best matches.
  const sortedDocuments = useMemo(() => {
    if (!documents.length) return documents;

    // Preserve relevance order when search is active and docs have scores
    const hasSearchScores = documents.some((d: any) => d.search_score !== undefined);
    if (searchTerm.trim().length >= 3 && hasSearchScores) {
      return documents; // Already sorted by relevance in loadDocuments
    }

    const sorted = [...documents];
    const desc = sortOrder.startsWith('-');
    const field = desc ? sortOrder.slice(1) : sortOrder;
    const isDateField = field === 'created_at' || field === 'published_at';
    const collator = new Intl.Collator('es', { sensitivity: 'base' });
    sorted.sort((a: any, b: any) => {
      // For date sorting, prefer published_at (real CMF date) over created_at (import date)
      const valA = isDateField ? a.published_at || a.created_at || '' : a[field] ?? '';
      const valB = isDateField ? b.published_at || b.created_at || '' : b[field] ?? '';
      const cmp = isDateField
        ? valA.localeCompare(valB) // ISO strings sort correctly with localeCompare
        : collator.compare(valA as string, valB as string);
      return desc ? -cmp : cmp;
    });
    return sorted;
  }, [documents, sortOrder, searchTerm]);

  // Cargar sugerencias de filtros
  const loadFilterSuggestions = useCallback(
    async (filterType: MentionType, search: string = '') => {
      if (!isMountedRef.current) return;

      if (isMountedRef.current) {
        setIsLoadingFilters(true);
      }
      try {
        const response = await apiService.getMentionSuggestions(filterType, search || '', {}, true);
        if (response.success && response.suggestions && isMountedRef.current) {
          const suggestions = response.suggestions
            .filter((s: any) => !s.is_mention_type)
            .map((s: any) => {
              const rawDisplay = (s.display || s.value).replace(/^@\w+:/, '');
              return {
                value: s.value,
                display: filterType === 'fecha' ? formatPeriodForFilter(rawDisplay) : rawDisplay,
                score: s.score,
              };
            });
          setFilterSuggestions(suggestions);
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError' && isMountedRef.current) {
          setFilterSuggestions([]);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingFilters(false);
        }
      }
    },
    [enableLogging]
  );

  // Cargar documentos cuando cambian los filtros o searchInContent
  useEffect(() => {
    if (!enabled) {
      setDocuments([]);
      setActiveScopeCount(null);
      return;
    }
    const timer = setTimeout(() => {
      loadDocuments('');
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.company,
    filters.document_type,
    filters.document_period,
    filters.shared_category,
    searchInContent,
    scopeFilter,
    enabled,
  ]);

  // Búsqueda con debounce
  // 500ms debounce to accommodate FAISS semantic search (~300ms backend)
  useEffect(() => {
    if (!enabled) return;
    const debounceMs = searchTerm.trim().length >= 3 ? 500 : 300;
    const timer = setTimeout(() => {
      loadDocuments(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchInContent, enabled]); // Incluir searchInContent para recargar cuando cambia

  // Toggle selección de documento
  const toggleDocumentSelection = useCallback(
    (documentId: string) => {
      // Usar refs para obtener los valores más recientes y evitar problemas de closure
      const currentIds = selectedDocumentIdsRef.current;
      const onSelect = onDocumentsSelectRef.current;

      if (!onSelect) {
        return;
      }

      const isSelected = currentIds.includes(documentId);
      const newSelection = isSelected
        ? currentIds.filter((id) => id !== documentId)
        : [...currentIds, documentId];

      // Add to recents when SELECTING (not deselecting)
      if (!isSelected) {
        const doc = documents.find((d) => d.id === documentId);
        if (doc) {
          setRecentDocs((prev) => {
            const filtered = prev.filter((r) => r.id !== doc.id);
            const entry: RecentDoc = {
              id: doc.id,
              name: doc.name || doc.original_filename || '',
              scope: doc.scope,
              company: doc.company_name,
              type: doc.document_type,
              period: doc.document_period,
            };
            const updated = [entry, ...filtered].slice(0, MAX_RECENT_DOCS);
            try {
              localStorage.setItem(RECENT_DOCS_KEY, JSON.stringify(updated));
            } catch {
              /* ignore */
            }
            return updated;
          });
        }
      }

      onSelect(newSelection);
    },
    [enableLogging, documents]
  );

  // Select a range of documents (for shift+click)
  const selectDocumentRange = useCallback(
    (documentIds: string[]) => {
      const currentIds = selectedDocumentIdsRef.current;
      const onSelect = onDocumentsSelectRef.current;
      if (!onSelect) return;

      const newIds = documentIds.filter((id) => !currentIds.includes(id));
      if (newIds.length === 0) return;

      // Add new docs to recents
      for (const id of newIds) {
        const doc = documents.find((d) => d.id === id);
        if (doc) {
          setRecentDocs((prev) => {
            const filtered = prev.filter((r) => r.id !== doc.id);
            const entry: RecentDoc = {
              id: doc.id,
              name: doc.name || doc.original_filename || '',
              scope: doc.scope,
              company: doc.company_name,
              type: doc.document_type,
              period: doc.document_period,
            };
            return [entry, ...filtered].slice(0, MAX_RECENT_DOCS);
          });
        }
      }

      const newSelection = [...currentIds, ...newIds];
      try {
        const allRecents = JSON.parse(localStorage.getItem(RECENT_DOCS_KEY) || '[]');
        localStorage.setItem(RECENT_DOCS_KEY, JSON.stringify(allRecents));
      } catch {
        /* ignore */
      }

      onSelect(newSelection);
    },
    [documents]
  );

  // Manejar filtros
  const handleFilterClick = useCallback(
    (filterType: MentionType) => {
      setFilterMode(filterType);
      loadFilterSuggestions(filterType, '');
    },
    [loadFilterSuggestions]
  );

  const handleFilterValueSelect = useCallback(
    (value: string) => {
      if (!filterMode) return;

      const newFilters = { ...filters };
      // Multi-select toggle for all filter types
      const toggleArray = (current: string | string[] | undefined, val: string) => {
        const arr = Array.isArray(current) ? current : current ? [current] : [];
        const idx = arr.indexOf(val);
        if (idx >= 0) {
          const updated = arr.filter((v) => v !== val);
          return updated.length > 0 ? updated : undefined;
        }
        return [...arr, val];
      };

      if (filterMode === 'empresa') {
        newFilters.company = toggleArray(newFilters.company, value);
      } else if (filterMode === 'tipo') {
        newFilters.document_type = toggleArray(newFilters.document_type, value);
      } else if (filterMode === 'fecha') {
        newFilters.document_period = toggleArray(newFilters.document_period, value);
      } else if (filterMode === 'sector') {
        newFilters.shared_category = toggleArray(newFilters.shared_category, value);
      }

      setFilters(newFilters);

      // Notificar cambios de filtros al padre
      if (onFiltersChange) {
        onFiltersChange(newFilters);
      }

      // También llamar onFilterSelect si está disponible (para compatibilidad)
      if (onFilterSelect) {
        onFilterSelect(filterMode, value);
      }

      // Multi-select: keep dropdown open for all filter types.
      // User closes via "Listo", Escape, or click-outside.
    },
    [filterMode, filters, onFiltersChange, onFilterSelect]
  );

  const handleRemoveFilter = useCallback(
    (filterType: MentionType, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();

      const newFilters = { ...filters };
      if (filterType === 'empresa') delete newFilters.company;
      else if (filterType === 'tipo') delete newFilters.document_type;
      else if (filterType === 'fecha') delete newFilters.document_period;
      else if (filterType === 'sector') delete newFilters.shared_category;

      setFilters(newFilters);

      // Notificar cambios de filtros al padre
      if (onFiltersChange) {
        onFiltersChange(newFilters);
      }

      // Recargar documentos sin el filtro removido
      loadDocuments('');
    },
    [filters, onFiltersChange, loadDocuments]
  );

  /** Remove a single company from the multi-select company filter */
  const handleRemoveCompanyValue = useCallback(
    (value: string, e?: React.MouseEvent) => {
      if (e) e.stopPropagation();
      const current = Array.isArray(filters.company)
        ? filters.company
        : filters.company
          ? [filters.company]
          : [];
      const updated = current.filter((c) => c !== value);
      const newFilters = { ...filters, company: updated.length > 0 ? updated : undefined };
      if (!newFilters.company) delete newFilters.company;
      setFilters(newFilters);
      if (onFiltersChange) onFiltersChange(newFilters);
      loadDocuments('');
    },
    [filters, onFiltersChange, loadDocuments]
  );

  const handleClearAllFilters = useCallback(() => {
    const newFilters: DocumentFilters = {};
    setFilters(newFilters);

    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }

    loadDocuments('');
  }, [onFiltersChange, loadDocuments]);

  const handleBackToDocuments = useCallback(() => {
    setFilterMode(null);
    setFilterSuggestions([]);
    setSearchTerm('');
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = documents.map((d) => d.id);
    const currentIds = selectedDocumentIdsRef.current;
    const onSelect = onDocumentsSelectRef.current;
    const newIds = [...new Set([...currentIds, ...allIds])];
    onSelect(newIds);
  }, [documents]);

  const handleDeselectAll = useCallback(() => {
    const onSelect = onDocumentsSelectRef.current;
    onSelect([]);
  }, []);

  // Formatear metadata del documento
  const formatDocumentMetadata = useCallback((doc: Document): string => {
    const parts: string[] = [];
    if (doc.company_name) parts.push(doc.company_name);
    if (doc.document_type) parts.push(doc.document_type);
    if (doc.document_period) parts.push(formatPeriod(doc.document_period, doc.document_type));
    if (doc.page_count) parts.push(`${doc.page_count} páginas`);
    return parts.join(' • ');
  }, []);

  const toArr = (v: string | string[] | undefined) => (Array.isArray(v) ? v : v ? [v] : []);
  const hasFilters = !!(
    toArr(filters.company).length > 0 ||
    toArr(filters.document_type).length > 0 ||
    toArr(filters.document_period).length > 0 ||
    toArr(filters.shared_category).length > 0
  );
  const selectedCount = selectedDocumentIds.length; // Usar el prop directamente para el renderizado

  return {
    // Estado
    documents: sortedDocuments,
    isLoading,
    searchTerm,
    setSearchTerm,
    filters,
    filterMode,
    filterSuggestions,
    isLoadingFilters,
    searchInContent,
    setSearchInContent, // Permite activación/desactivación manual si es necesario
    scopeFilter,
    setScopeFilter,
    recentDocs,
    inactiveScopeCount,
    activeScopeCount,
    sortOrder,
    setSortOrder,

    // Funciones de documentos
    loadDocuments,
    toggleDocumentSelection,
    selectDocumentRange,
    handleSelectAll,
    handleDeselectAll,

    // Funciones de filtros
    handleFilterClick,
    handleFilterValueSelect,
    handleRemoveFilter,
    handleRemoveCompanyValue,
    handleClearAllFilters,
    handleBackToDocuments,
    loadFilterSuggestions,

    // Utilidades
    formatDocumentMetadata,
    hasFilters,
    selectedCount,
  };
};
